import { useContext, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from 'components/Container';
import { SEO } from 'components/SEO';
import { createFAQSchema } from 'components/SEO/structuredData';
import { FaqCategory, FaqItem, Headline } from './components';
import { SchoolContext } from 'context/SchoolContext';
import Divider from '@mui/material/Divider';
import Contact from '../components/Contact';
import Stack from '@mui/material/Stack';

function Faq(): JSX.Element {
  const theme = useTheme();
  const schoolContext = useContext(SchoolContext);

  // Generate FAQ structured data
  const faqStructuredData = useMemo(() => {
    if (!schoolContext?.faq.categories) return null;

    const allFAQs = schoolContext.faq.categories.flatMap(category =>
      category.questions.map(q => ({
        question: q.question,
        answer: q.answer,
      }))
    );

    return createFAQSchema(allFAQs);
  }, [schoolContext?.faq.categories]);

  // Generate keywords for FAQ page
  const faqKeywords = useMemo(() => {
    const baseKeywords = [
      'driving school FAQ',
      'driver education questions',
      'driving lessons FAQ',
      'drivers license questions',
      'driving school questions',
      'behind the wheel training FAQ',
    ];

    if (schoolContext?.name) {
      baseKeywords.push(`${schoolContext.name} FAQ`);
    }

    if (schoolContext?.locations) {
      schoolContext.locations.forEach(location => {
        if (location.city) {
          baseKeywords.push(`driving school FAQ ${location.city}`);
        }
      });
    }

    return baseKeywords;
  }, [schoolContext]);

  return (
    <>
      <SEO
        title="Frequently Asked Questions"
        description={
          schoolContext?.faq.subtitle ||
          'Get answers to common questions about our driver education programs, driving lessons, licensing requirements, and more.'
        }
        keywords={faqKeywords}
        ogType="article"
        ogImage={schoolContext?.faq.bannerImage || schoolContext?.logoImage}
        ogImageAlt="Frequently Asked Questions about Driver Education"
        structuredData={faqStructuredData ? [faqStructuredData] : undefined}
      />
      <Box>
        <Box
          sx={{
            backgroundColor: theme.palette.alternate.main,
            backgroundImage: `linear-gradient(120deg, ${theme.palette.background.paper} 0%, ${theme.palette.alternate.main} 100%)`,
            marginTop: -13,
            paddingTop: 13,
          }}
        >
          <Container>
            <Headline
              title={schoolContext?.faq.title ?? 'Have a question?'}
              subtitle={schoolContext?.faq.subtitle}
              image={schoolContext?.faq.bannerImage}
            />
          </Container>
        </Box>
        <Container maxWidth={800}>
          {schoolContext?.faq.categories.map((category) => (
            <FaqCategory name={category.name} key={category.name} marginBottom={8}>
              {category.questions.map((question, index) => (
                <FaqItem question={question.question} answer={question.answer} key={index} />
              ))}
            </FaqCategory>
          ))}
        </Container>
        <Box bgcolor={theme.palette.alternate.main}>
          <Container>
            {/* <Footer /> */}
            <Stack gap={2} divider={<Divider flexItem />}>
              {schoolContext?.locations?.map((location, index) => (<Contact {...location} key={index} />))}
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  );
}

export default Faq;
